// @/utils/pdfQuotationEstimateGenerator.js
import PDFQuotationBase from "./pdfQuotationBase";

/**
 * PDFQuotationEstimateGenerator
 * สร้าง docDefinition สำหรับ Fee Estimate
 * (ตรงกับตัวอย่าง "Quotation / ใบเสนอค่าวิชาชีพกฎหมาย" หน้าที่ 2 —
 *  มีแถว "Fee Basis" ตัวหนา, คอลัมน์เป็น "Estimated Amount", ไม่มี VAT,
 *  สรุปเป็นแถวเดียว "Estimated Professional Fees" + ข้อความ budgeting-only,
 *  และ Quotation Terms มี 3 ย่อหน้า)
 *
 * หมายเหตุ: ไม่มีกล่องเตือนสีส้ม (Non-binding Estimate) ต่อจากหัวเรื่องแบบที่เคยมี
 * เพราะแบบฟอร์มต้นฉบับ (หน้าที่ 2) ไม่มีองค์ประกอบนี้
 */
const DEFAULT_COMPANY_DISPLAY_NAME = "Dej-Udom & Associates";
// ✅ เปลี่ยนจาก array ตายตัวเป็นฟังก์ชัน แทรกชื่อบริษัทที่เลือกจริง (company_display_name)
// ให้ตรงกับหัวเอกสาร — ถ้าไม่ได้ส่ง company_display_name มา fallback เป็นชื่อเดิม
const buildDefaultTerms = (companyDisplayName) => {
    const name = companyDisplayName || DEFAULT_COMPANY_DISPLAY_NAME;
    return [
        "This quotation provides an estimate of professional fees based on the anticipated scope of work and estimated time required. Professional fees will be charged based on the actual time spent by the lawyers and personnel involved at the applicable hourly rates.",
        `The estimated fee is provided for budgeting purposes only and does not constitute a fixed or capped fee. If the fees are likely to materially exceed the estimate, ${name} will notify the client before undertaking substantial additional work.`,
        "The estimated fees exclude VAT, government fees, official fees, translation fees, travel expenses, courier charges, and other out-of-pocket expenses, where applicable.",
    ];
};

const DEFAULT_FEE_BASIS_LABEL = "Fee Basis: Hourly Rate / หลักการคิดค่าบริการ: ตามจำนวนชั่วโมงที่ใช้จริง";
const DEFAULT_ESTIMATE_NOTE = "Exclusive of VAT and applicable disbursements.";
const DEFAULT_BUDGET_NOTE = "Estimated for budgeting purposes only. Final fees will be based on the actual time spent.";

export default class PDFQuotationEstimateGenerator extends PDFQuotationBase {
    /**
     * แถวหัวข้อ "Fee Basis: Hourly Rate / ..." ตัวหนา อยู่คอลัมน์ซ้าย คอลัมน์ขวาว่าง
     */
    buildFeeBasisRow(rawHeader) {
        return [
            { text: rawHeader.fee_basis_label || DEFAULT_FEE_BASIS_LABEL, fontSize: 9, bold: true, margin: [5, 5, 5, 5] },
            { text: "", margin: [5, 5, 5, 5] },
        ];
    }

    /**
     * แถวข้อความตัวเอียงเล็กๆ ก่อนเข้าสรุปยอด เช่น "Exclusive of VAT and applicable disbursements."
     */
    buildEstimateNoteRow(rawHeader) {
        return [
            {
                text: rawHeader.estimate_note || DEFAULT_ESTIMATE_NOTE,
                fontSize: 8,
                italics: true,
                bold: true,
                margin: [5, 3, 5, 2],
            },
            { text: "", margin: [5, 3, 5, 2] },
        ];
    }

    /**
     * แปลงค่าที่อาจเป็น string มี comma / ข้อความว่างเปล่า ("", "ไม่ระบุ") ให้เป็นตัวเลข
     * คืนค่า null ถ้า parse ไม่ได้หรือ <= 0 (ใช้เพื่อรู้ว่า "ไม่ได้ระบุวงเงิน" จริงๆ)
     */
    parseNumericLoose(value) {
        if (value === null || value === undefined || value === "") return null;
        const num = Number(String(value).replace(/,/g, "").trim());
        if (isNaN(num) || num <= 0) return null;
        return num;
    }

    /**
     * รวมยอดจริงของทุกรายการ — รองรับทั้ง item.amount (ตัวเลข) และ item.rate (string
     * ที่ format ไว้แล้ว เช่น "1,500.00") คืนค่า 0 ถ้าไม่มีรายการหรือ parse ไม่ได้เลย
     */
    sumItemAmounts(items) {
        return (items || []).reduce((sum, item) => {
            if (item.amount !== undefined && item.amount !== null) {
                return sum + (Number(item.amount) || 0);
            }
            return sum + (this.parseNumericLoose(item.rate) || 0);
        }, 0);
    }

    /**
     * ยอดที่โชว์ในแถว "Estimated Professional Fees" — ใช้ "ผลรวมค่าจริงของทุกรายการ"
     * เป็นตัวเลขหลัก (แทนที่วงเงินไม่เกิน/not_to_exceed_cap ตามที่ปรับล่าสุด) ลำดับการ
     * เลือกค่า:
     * 1. rawHeader.estimated_amount_text — string สำเร็จรูป เผื่อ override ตรงๆ ในอนาคต
     * 2. rawHeader.estimate_min / estimate_max — ช่วงราคา เผื่อ use case อื่นในอนาคต
     * 3. ผลรวมค่าจริงของทุกรายการ (sumItemAmounts) — ค่าหลักที่ใช้จริงตอนนี้
     * 4. rawHeader.not_to_exceed_cap — fallback เผื่อไม่มีรายการ/รวมได้ 0
     * 5. fallback สุดท้าย: rawHeader.grand_total — กันเอกสารพัง
     */
    resolveEstimatedAmountText(rawHeader, items) {
        if (rawHeader.estimated_amount_text) return rawHeader.estimated_amount_text;
        if (rawHeader.estimate_min !== undefined && rawHeader.estimate_max !== undefined) {
            return `${this.formatNumber(rawHeader.estimate_min)} - ${this.formatNumber(rawHeader.estimate_max)}`;
        }
        const itemsTotal = this.sumItemAmounts(items);
        if (itemsTotal > 0) return this.formatNumber(itemsTotal);
        const cap = this.parseNumericLoose(rawHeader.not_to_exceed_cap);
        if (cap !== null) return this.formatNumber(cap);
        return this.formatNumber(rawHeader.grand_total);
    }

    /**
     * แถวสรุปยอด: "Estimated Professional Fees" (ผลรวมค่าจริงของรายการ) + แถว note
     * budgeting-only ต่อท้ายในตารางรายการเดียวกัน (ไม่ใช่ตารางแยก) เพื่อไม่ให้เกิดเส้นซ้อน
     * กันตรงรอยต่อ
     * @returns {{rows: Array, heights: Array<number>}}
     */
    buildSummaryRows(rawHeader, items) {
        const showCurrency = rawHeader.currency || "THB";
        const amountText = this.resolveEstimatedAmountText(rawHeader, items);

        const rows = [];
        const heights = [];

        const hasVat = rawHeader.vat_percent !== undefined && rawHeader.vat_percent !== null && rawHeader.vat_percent !== "";
        const vatBase = Number(rawHeader.vat_base) || 0;
        const noVatBase = Number(rawHeader.no_vat_base) || 0;
        const vatAmount = Number(rawHeader.vat_amount) || 0;
        // แสดงแถวย่อยเฉพาะเมื่อมีรายการที่ไม่คิด VAT จริง
        const showBreakdown = hasVat && noVatBase > 0;

        // ===== หมายเหตุอัตรา VAT — วางเหนือแถวสรุปยอด =====
        // ⚠️ ใช้ 2 cells แยก (ไม่ใช่ colSpan) เพื่อให้เส้นแนวตั้งกลางตารางยังลากต่อเนื่อง
        if (hasVat) {
            rows.push([
                {
                    text: `VAT at ${this.formatNumber(rawHeader.vat_percent)}% applies to all items unless marked "Not subject to VAT".`,
                    fontSize: 8,
                    italics: true,
                    margin: [5, 3, 5, 3],
                },
                { text: "", margin: [5, 3, 5, 3] },
            ]);
            heights.push(20);
        }

        // ===== ยอดค่าบริการก่อน VAT =====
        rows.push([
            {
                text: "Estimated Professional Fees / ค่าบริการโดยประมาณ",
                fontSize: 11,
                bold: true,
                fillColor: "#eeeeee",
                margin: [5, 4, 5, 2],
            },
            {
                text: `${amountText} ${showCurrency}`,
                fontSize: 11,
                bold: true,
                alignment: "right",
                fillColor: "#eeeeee",
                margin: [5, 4, 5, 2],
            },
        ]);
        heights.push(24);

        // ===== แถวย่อยแยกยอดที่คิด/ไม่คิด VAT =====
        if (showBreakdown) {
            rows.push([
                { text: "VATable Amount / ยอดที่คิด VAT", fontSize: 9, margin: [20, 2, 5, 2] },
                {
                    text: `${this.formatNumber(vatBase)} ${showCurrency}`,
                    fontSize: 9,
                    alignment: "right",
                    margin: [5, 2, 5, 2],
                },
            ]);
            heights.push(20);

            rows.push([
                { text: "Not subject to VAT / ยอดที่ไม่คิด VAT", fontSize: 9, margin: [20, 2, 5, 2] },
                {
                    text: `${this.formatNumber(noVatBase)} ${showCurrency}`,
                    fontSize: 9,
                    alignment: "right",
                    margin: [5, 2, 5, 2],
                },
            ]);
            heights.push(20);
        }

        // ===== VAT =====
        if (hasVat) {
            const vatLabel = showBreakdown
                ? `VAT ${this.formatNumber(rawHeader.vat_percent)}% on ${this.formatNumber(vatBase)} / ภาษีมูลค่าเพิ่ม ${this.formatNumber(rawHeader.vat_percent)}%`
                : `VAT ${this.formatNumber(rawHeader.vat_percent)}% / ภาษีมูลค่าเพิ่ม ${this.formatNumber(rawHeader.vat_percent)}%`;

            rows.push([
                { text: vatLabel, fontSize: 10, margin: [5, 3, 5, 3] },
                {
                    text: `${this.formatNumber(vatAmount)} ${showCurrency}`,
                    fontSize: 10,
                    alignment: "right",
                    margin: [5, 3, 5, 3],
                },
            ]);
            heights.push(24);

            // ===== ยอดรวมโดยประมาณ (รวม VAT แล้ว) =====
            rows.push([
                {
                    text: "Estimated Total / ยอดรวมโดยประมาณ",
                    fontSize: 11,
                    bold: true,
                    fillColor: "#eeeeee",
                    margin: [5, 4, 5, 2],
                },
                {
                    text: `${this.formatNumber(Number(rawHeader.grand_total) || 0)} ${showCurrency}`,
                    fontSize: 11,
                    bold: true,
                    alignment: "right",
                    fillColor: "#eeeeee",
                    margin: [5, 4, 5, 2],
                },
            ]);
            heights.push(26);
        }

        // ===== หมายเหตุท้ายตาราง =====
        rows.push([
            {
                text: rawHeader.budget_note || DEFAULT_BUDGET_NOTE,
                fontSize: 9,
                alignment: "center",
                fillColor: "#eeeeee",
                margin: [5, 3, 5, 4],
                colSpan: 2,
            },
            {},
        ]);
        heights.push(20);

        return { rows, heights };
    }

    generateDocDefinition(quotationData) {
        const rawHeader = quotationData?.header || {};
        const items = quotationData?.items || [];
        const header = this.buildCompanyHeader(rawHeader);

        const docTitle = "Quotation / ใบเสนอค่าวิชาชีพกฎหมาย";
        header.doc_title_en = "Quotation No";
        header.doc_title_th = "เลขที่ใบเสนอราคา";
        header.doc_no = rawHeader.doc_no;
        header.doc_date = rawHeader.doc_date;
        header.currency = rawHeader.currency;
        header.vat_percent = rawHeader.vat_percent;

        // ⚠️ หมายเหตุสำคัญ (แก้บั๊กหลัก): เวอร์ชันก่อนหน้าใช้ absolutePosition วางกล่อง
        // ตารางที่ตำแหน่งคงที่ โดยกะประมาณความสูงของ header/title/client-info เอง เพื่อให้
        // กล่องดูเต็มหน้าเหมือนแบบฟอร์มต้นฉบับ — ทดสอบจริงด้วย pdfmake (Node) แล้วพบว่า
        // องค์ประกอบที่ตั้ง absolutePosition ยังถูกนับความสูงเข้าไปใน "การไหลของเอกสาร"
        // (document flow) ที่ pdfMake ใช้ตัดสินใจขึ้นหน้าใหม่อยู่ดี ทำให้เกิดหน้าเปล่าๆ
        // แทรกมาโดยไม่คาดคิด (เจอแม้กับข้อมูลแค่ 5 รายการ) — เปลี่ยนมาใช้ table ปกติแบบ
        // ไหลตามเนื้อหาจริง (ไม่มี absolutePosition, ไม่ต้องกะความสูงเป็นตัวเลขคงที่เอง)
        // ให้ pdfMake จัดการการขึ้นหน้าใหม่/ความสูงเองทั้งหมด — ถูกต้องกว่าและไม่มีบั๊กนี้อีก
        const COL1_WIDTH = 340;
        const COL2_WIDTH = 203;

        // ===== หัวตาราง + Fee Basis (ตาราง 2 แถว อยู่ในลำดับ flow ปกติ) =====
        const headerTable = {
            // กันไม่ให้หัวตาราง/แถว Fee Basis ถูกตัดแยกข้ามหน้ากลางคัน
            unbreakable: true,
            table: {
                widths: [COL1_WIDTH, COL2_WIDTH],
                body: [
                    [
                        { text: "Description / รายการ", fontSize: 10, bold: true, margin: [5, 5, 5, 5] },
                        {
                            text: "Estimated Amount / จำนวนเงินโดยประมาณ",
                            fontSize: 8,
                            bold: true,
                            alignment: "right",
                            margin: [5, 8, 5, 8],
                        },
                    ],
                    this.buildFeeBasisRow(rawHeader),
                ],
            },
            layout: {
                // เส้นบนสุดของกรอบ + เส้นใต้หัวตารางเท่านั้น — เส้นใต้แถว Fee Basis วาดแยก
                // ด้วย canvas เฉพาะความกว้างคอลัมน์ Description (ดูตัวแปร feeBasisDivider
                // ด้านล่าง) ไม่มีเส้นขอบล่างของตารางนี้ (ต่อกับตารางรายการแบบไร้รอยต่อ)
                hLineWidth: (i) => (i === 0 || i === 1 ? 1 : 0),
                vLineWidth: () => 1,
                hLineColor: () => "black",
                vLineColor: () => "black",
                paddingLeft: () => 0,
                paddingRight: () => 0,
                paddingTop: () => 0,
                paddingBottom: () => 0,
            },
        };

        // เส้นใต้แถว "Fee Basis" — แสดงเฉพาะความกว้างคอลัมน์ Description / รายการ (ไม่ลาก
        // ยาวไปถึงคอลัมน์ Estimated Amount) วาดด้วย canvas แยกต่างหาก เพราะ pdfMake layout
        // hLineWidth ควบคุมได้แค่เส้นเต็มความกว้างแถวเท่านั้น — อยู่ในลำดับ flow ปกติ (ไม่ใช้
        // absolutePosition) จึงเลื่อนตามตำแหน่งจริงเสมอไม่ว่าเนื้อหาก่อนหน้าจะสูงเท่าไหร่
        const feeBasisDivider = {
            canvas: [{ type: "line", x1: 0, y1: 0, x2: COL1_WIDTH + 2, y2: 0, lineWidth: 1 }],
            margin: [0, 0, 0, 0],
        };

        // ===== ตารางสรุป: note + Estimated Professional Fees + budgeting note =====
        // ไม่มีเส้นขอบบน (ตามที่ขอให้เอาออก) มีเส้นคั่นระหว่างแถว + เส้นปิดขอบล่างกรอบ
        // unbreakable: true กัน 3 แถวนี้ถูกตัดแยกข้ามหน้า (ถ้าที่ไม่พอ ทั้งก้อนจะเลื่อนไป
        // หน้าถัดไปพร้อมกันแทน ไม่ใช่ตัดกลางแถวแบบที่เคยเจอ)
        const { rows: summaryRows, heights: summaryHeights } = this.buildSummaryRows(rawHeader, items);
        const extraRows = [...summaryRows];
        const extraHeights = [...summaryHeights];
        const summaryBlockHeight = extraHeights.reduce((s, h) => s + h, 0);

        const summaryTable = {
            unbreakable: true,
            table: {
                widths: [COL1_WIDTH, COL2_WIDTH],
                body: extraRows,
                heights: extraHeights,
            },
            layout: {
                hLineWidth: (i) => (i === 0 ? 0 : 1),
                vLineWidth: () => 1,
                hLineColor: () => "black",
                vLineColor: () => "black",
                paddingLeft: () => 0,
                paddingRight: () => 0,
                paddingTop: () => 0,
                paddingBottom: () => 0,
            },
        };

        // ⚠️ summaryTable และ Quotation Terms เป็น 2 ก้อนอิสระต่อกัน (ไม่รวมเป็นก้อนเดียว)
        // แต่ละก้อน unbreakable ของตัวเอง — ถ้าที่ว่างพอสำหรับทั้งคู่ จะอยู่หน้าเดียวกัน
        // (ดันลงไปติดขอบล่างด้วยกัน) แต่ถ้าที่ว่างพอแค่ summaryTable ก้อนเดียว Terms จะ
        // ล้นไปหน้าใหม่ทั้งก้อนของมันเอง (ไม่ได้ถูกลากตามไปด้วยทั้งที่ summary ยังอยู่ได้)
        const terms = rawHeader.quotation_terms || buildDefaultTerms(rawHeader.company_display_name);
        const termsBlockContent = this.generateTermsBlock(terms);
        termsBlockContent.unbreakable = true;
        // ประมาณความสูงของ Quotation Terms แบบเผื่อเหลือเผื่อขาด (เนื้อหาจริงยาว/สั้นตาม
        // ข้อความที่ผู้ใช้กำหนดได้ กะเป๊ะไม่ได้) ใช้แค่สำหรับคำนวณว่าจะดันก้อนนี้ลงไปกี่ pt
        // ถ้าประมาณคลาดเคลื่อนบ้าง แค่ทำให้ก้อนนี้ไม่ได้ชิดขอบล่างเป๊ะ (เรื่องเล็ก) ไม่ใช่
        // การทำให้ล้นหน้าไปแบบเดิม
        const TERMS_HEIGHT_ESTIMATE = this.estimateTermsHeight(terms);
        // ⚠️ แบ่งรายการเป็น "หน้า" เอง ล่วงหน้า (ไม่พึ่งการขึ้นหน้าใหม่อัตโนมัติกลางตาราง
        //   1. หน้าที่ไม่ใช่หน้าสุดท้าย ต้องมีเส้นปิดขอบล่างของตารางชัดเจน — ทำได้เพราะ
        //      แต่ละหน้าคือตาราง "คนละก้อน" จริงๆ ไม่ใช่ตารางเดียวที่ pdfMake ตัดกลางเอง
        //      (ซึ่งเราควบคุมเส้นตรงจุดตัดกลางไม่ได้)
        //   2. หน้าที่ไม่ใช่หน้าสรุปผลรวม ต้องใช้พื้นที่เต็มหน้าจริง — เพราะไม่ต้องเผื่อ
        //      margin ก้อนใหญ่สำหรับ footer/summary อีกต่อไป (เลิกใช้ footer+background
        //      แบบเดิมทั้งหมด ใช้ pageMargins ปกติเท่ากันทุกด้าน)
        // วิธีคำนวณ: ประมาณพื้นที่ว่างที่มีจริงต่อหน้า (หน้าแรกหักพื้นที่ header/title/
        // client-info/headerTable ออกก่อน, หน้าอื่นเริ่มจากขอบบนเลย) แล้ววนใส่รายการทีละ
        // แถวจนกว่าจะเต็มพื้นที่หน้านั้นถึงค่อยขึ้นหน้าใหม่ — หน้าสุดท้ายเผื่อพื้นที่ให้
        // summaryTable ไว้ด้วย (กันสรุปยอดล้นออกนอกพื้นที่ที่ประมาณไว้)
        const PAGE_HEIGHT = 841.89; // A4 (pt)
        const TOP_MARGIN = 25;
        const BOTTOM_MARGIN = 25;
        const COMPANY_HEADER_HEIGHT = 80;
        const TITLE_HEIGHT = 30;
        const DIVIDER_HEIGHT = 11;
        const CLIENT_INFO_HEIGHT = this.estimateClientInfoHeight(header);
        const SAFETY_BUFFER = 6;
        const HEADER_TABLE_HEIGHT = 26 + 22; // แถวหัวตาราง + แถว Fee Basis
        const FEE_BASIS_DIVIDER_HEIGHT = 1;
        // ⚠️ เผื่อ safety margin กันการประมาณความสูงคลาดเคลื่อนเล็กน้อย (เช่น ฟอนต์จริง
        // อาจกินพื้นที่มากกว่าที่กะไว้ 2-3pt) ถ้าไม่เผื่อไว้ รายการอาจ "ล้นเอง" เกินพื้นที่
        // ที่กะไว้แบบพอดีเป๊ะ ทำให้เกิดการขึ้นหน้าใหม่แบบไม่ตั้งใจ ซ้อนกับ pageBreak ที่เรา
        // กำหนดเอง จนได้หน้าเปล่าๆ /เศษเนื้อหาแทรกมาโดยไม่คาดคิด
        const PAGE_SAFETY_MARGIN = 35;


        const page1PrefixHeight =
            TOP_MARGIN +
            COMPANY_HEADER_HEIGHT +
            TITLE_HEIGHT +
            DIVIDER_HEIGHT +
            CLIENT_INFO_HEIGHT +
            SAFETY_BUFFER +
            HEADER_TABLE_HEIGHT +
            FEE_BASIS_DIVIDER_HEIGHT;
        const page1Available = PAGE_HEIGHT - page1PrefixHeight - BOTTOM_MARGIN - PAGE_SAFETY_MARGIN;
        const continuationAvailable = PAGE_HEIGHT - TOP_MARGIN - BOTTOM_MARGIN - PAGE_SAFETY_MARGIN;
        // ⚠️ เผื่อ safety margin กันการประมาณความสูงคลาดเคลื่อนเล็กน้อย (เช่น ฟอนต์จริง
        // อาจกินพื้นที่มากกว่าที่กะไว้ 2-3pt) ถ้าไม่เผื่อไว้ รายการอาจ "ล้นเอง" เกินพื้นที่
        // ที่กะไว้แบบพอดีเป๊ะ ทำให้เกิดการขึ้นหน้าใหม่แบบไม่ตั้งใจ ซ้อนกับ pageBreak ที่เรา
        // กำหนดเอง จนได้หน้าเปล่าๆ /เศษเนื้อหาแทรกมาโดยไม่คาดคิด

        const itemRows = this.buildItemRows(items);
        const rowHeights = this.buildRowHeights(items, COL1_WIDTH);

        // แบ่งรายการเป็นกลุ่มตามหน้า: pageGroups[i] = { startIdx, endIdx, height, available }
        const pageGroups = [];
        let startIdx = 0;
        let currentHeight = 0;
        let available = page1Available;
        for (let idx = 0; idx < items.length; idx++) {
            const h = rowHeights[idx];
            if (idx > startIdx && currentHeight + h > available) {
                pageGroups.push({ startIdx, endIdx: idx, height: currentHeight, available });
                startIdx = idx;
                currentHeight = 0;
                available = continuationAvailable;
            }
            currentHeight += h;
        }
        pageGroups.push({ startIdx, endIdx: items.length, height: currentHeight, available });

        // ⚠️ ตรวจสอบว่า summaryTable (unbreakable) จะพอดีกับพื้นที่ว่างจริงของหน้าที่กะไว้
        // ว่าเป็น "หน้าสุดท้าย" หรือไม่ (เช็คแค่ summaryTable เพราะ Terms แยกอิสระ ล้นเองได้
        // โดยไม่ต้องลาก summary ตามไปด้วย) — ถ้าพื้นที่ไม่พอแม้แต่ summaryTable ก้อนเดียว มัน
        // จะเด้งไปหน้าใหม่เอง (พฤติกรรมของ unbreakable) ทำให้หน้าที่กะไว้ผิดจากหน้าที่มันไป
        // โผล่จริง ส่งผลให้หน้านั้นไม่มีเส้นปิดขอบล่าง (เพราะคิดว่าตัวเองเป็นหน้าสุดท้าย) ทั้ง
        // ที่ไม่ใช่แล้ว — แก้โดยเพิ่มหน้าว่างใหม่สำหรับ summaryTable โดยเฉพาะล่วงหน้า
        const lastGroup = pageGroups[pageGroups.length - 1];
        if (lastGroup.available - lastGroup.height < summaryBlockHeight) {
            pageGroups.push({
                startIdx: lastGroup.endIdx,
                endIdx: lastGroup.endIdx,
                height: 0,
                available: continuationAvailable,
            });
        }

        const itemPageTables = pageGroups.map((pg, idx) => {
            const isFirstGroup = idx === 0;
            const isLastGroup = idx === pageGroups.length - 1;
            const pageRows = itemRows.slice(pg.startIdx, pg.endIdx);
            const pageHeights = rowHeights.slice(pg.startIdx, pg.endIdx);
            // หน้าสุดท้าย: เช็คว่าที่ว่างพอสำหรับทั้ง summaryTable และ Terms ไหม
            //   - พอทั้งคู่ -> เติมที่ว่างเผื่อทั้งคู่ ดันลงไปติดขอบล่างด้วยกัน (อยู่หน้าเดียวกัน)
            //   - พอแค่ summaryTable -> เติมที่ว่างเผื่อแค่ summaryTable ดันมันลงไปติดขอบล่าง
            //     ส่วน Terms (unbreakable ของตัวเอง) จะไม่พอที่เหลือ แล้วล้นไปหน้าใหม่เอง
            // หน้าอื่นๆ ที่ไม่ใช่หน้าสุดท้าย เติมที่ว่างให้เต็มพื้นที่ตามปกติ
            let fillerHeight;
            if (isLastGroup) {
                const slack = pg.available - pg.height;
                const bothFit = slack - summaryBlockHeight >= TERMS_HEIGHT_ESTIMATE;
                fillerHeight = bothFit
                    ? slack - summaryBlockHeight - TERMS_HEIGHT_ESTIMATE
                    : Math.max(slack - summaryBlockHeight, 0);
            } else {
                fillerHeight = Math.max(pg.available - pg.height, 0);
            }

            const table = {
                table: {
                    widths: [COL1_WIDTH, COL2_WIDTH],
                    body: [
                        ...pageRows,
                        [
                            { text: "", margin: [5, 3, 5, 3] },
                            { text: "", margin: [5, 3, 5, 3] },
                        ],
                    ],
                    heights: (i) => (i === pageRows.length ? fillerHeight : "auto"),
                },
                layout: {
                    // หน้าแรกไม่ต้องมีเส้นบน (ต่อจาก feeBasisDivider ด้านบนแบบไร้รอยต่อ)
                    // หน้าอื่นๆ (ขึ้นหน้าใหม่) มีเส้นบนปิดกรอบให้เห็นชัดว่าเริ่มกล่องใหม่
                    // หน้าที่ไม่ใช่หน้าสุดท้าย ปิดเส้นขอบล่างให้สมบูรณ์ (ตามที่ขอ)
                    // หน้าสุดท้ายเปิดขอบล่างไว้ ต่อกับ summaryTable แบบไร้รอยต่อ
                    hLineWidth: (i, node) => {
                        if (i === 0) return isFirstGroup ? 0 : 1;
                        if (i === node.table.body.length) return isLastGroup ? 0 : 1;
                        return 0;
                    },
                    vLineWidth: () => 1,
                    hLineColor: () => "black",
                    vLineColor: () => "black",
                    paddingLeft: () => 0,
                    paddingRight: () => 0,
                    paddingTop: () => 0,
                    paddingBottom: () => 0,
                },
            };
            if (!isFirstGroup) table.pageBreak = "before";
            return table;
        });


        const additionalInfoBlock = this.generateAdditionalInfoBlock(rawHeader.additional_info);

        const content = [
            this.generateCompanyHeaderBlock(header),
            this.generateTitleBlock(header, docTitle),
            this.generateDividerLine(),
            this.generateClientInfoBlock(header),
            headerTable,
            feeBasisDivider,
            ...itemPageTables,
            summaryTable,
            termsBlockContent,
        ];

        // ✅ Additional Information — ขึ้นหน้าใหม่ต่อจากหน้าสุดท้ายเสมอ (ถ้ามีข้อความ)
        if (additionalInfoBlock) content.push(additionalInfoBlock);

        return {
            pageSize: "A4",
            pageMargins: [TOP_MARGIN, TOP_MARGIN, TOP_MARGIN, BOTTOM_MARGIN],
            content,
            defaultStyle: {
                font: "Sarabun",
                fontSize: 10,
            },
        };
    }
}